import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { Icon } from '../Icon';
import { useStore } from '@store/StoreProvider';
import {
  LANDING_PRICING,
  formatMoney,
  formatMoneyDual,
  pluralRu,
  rubPart,
} from '../pricingConfig';

type PlanId = keyof typeof LANDING_PRICING.plans;

type PlanCard = {
  id: PlanId;
  name: string;
  desc: string;
  features: string[];
  popular?: boolean;
};

export const PricingSection: React.FC = observer(() => {
  const [months, setMonths] = useState(1);
  const [ops, setOps] = useState(1);
  const { auth, i18n } = useStore();
  const isLoggedIn = auth.isAuthenticated;
  void i18n.language
  void i18n.version

  const plans: PlanCard[] = [
    {
      id: 'start',
      name: i18n.t('billing.plans.name_start'),
      desc: 'Один канал, чтобы начать отвечать клиентам.',
      features: [
        '1 канал связи',
        'До 3 регионов на канал',
        'Шаблоны: 3 общих + 3 личных',
        'Файлы: PNG, JPG, PDF',
        'Общий архив диалогов',
      ],
    },
    {
      id: 'pro',
      name: i18n.t('billing.plans.name_pro'),
      desc: 'Несколько каналов и команда операторов.',
      popular: true,
      features: [
        '3 канала связи',
        'До 5 регионов на канал',
        'Шаблоны: 6 общих + 6 личных',
        'Расширенная кастомизация виджета',
        'Роли и доступы для команды',
        'Всё из Старта',
      ],
    },
    {
      id: 'max',
      name: i18n.t('billing.plans.name_max'),
      desc: 'Каналы, регионы и шаблоны без ограничений.',
      features: [
        'Каналы связи без ограничений',
        'Регионы без ограничений',
        'Шаблоны без ограничений',
        'Приоритетная поддержка',
        'Всё из Профи',
      ],
    },
  ];

  const period = useMemo(
    () => LANDING_PRICING.periods.find((p) => p.m === months) ?? LANDING_PRICING.periods[0],
    [months],
  );

  const setOpsSafe = (n: number) => {
    if (Number.isNaN(n)) {
      setOps(1);
      return;
    }
    setOps(Math.max(1, Math.min(LANDING_PRICING.maxOps, Math.round(n))));
  };

  const ctaLabel = 'Попробовать 7 дней'
  const perOpLabel = '/мес за оператора'

  return (
    <section id="pricing" className="relative pt-14 sm:pt-20 pb-10 sm:pb-12 overflow-hidden">
      <div className="land-desktop-block absolute inset-0 pointer-events-none">
        <div className="floaty absolute left-[4%] top-1/3 w-12 h-12 rounded-2xl border border-brand2/15" />
        <div className="floaty slow absolute right-[5%] top-24 w-16 h-16 rounded-full border border-indigo-400/15" />
        <div className="floaty rev absolute right-[7%] bottom-24 w-8 h-8 rotate-45 border border-cyan-400/20" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="reveal inline-flex items-center gap-2 text-xs font-semibold text-brand2 uppercase tracking-widest mb-4">
            <span className="w-8 h-px bg-brand2" /> {i18n.t('nav.plans')} <span className="w-8 h-px bg-brand2" />
          </div>
          <h2 className="reveal text-3xl sm:text-5xl font-extrabold">
            Платите за операторов.{' '}
            <span className="text-gradient">Ровно за столько, сколько нужно.</span>
          </h2>
          <p className="reveal mt-3 text-slate-400">
            Одна подписка — один оператор. Нужно больше — добавляете подписки. Тариф определяет
            возможности, а не размер команды.
          </p>
        </div>

        <div className="reveal mb-9 flex flex-col lg:flex-row items-stretch lg:items-center justify-center gap-3">
          <div className="bill-switch p-1 rounded-2xl glass border border-white/10 text-sm font-semibold">
            {LANDING_PRICING.periods.map((p) => {
              const on = p.m === months;
              return (
                <button
                  key={p.m}
                  type="button"
                  data-period={p.m}
                  onClick={() => setMonths(p.m)}
                  className={`per rounded-xl transition ${on ? 'bg-brand text-white' : 'text-slate-300'}`}
                >
                  {p.m === 1 && '1 мес'}
                  {p.m === 3 && (
                    <>
                      3 мес <span className="text-brand2">−10%</span>
                    </>
                  )}
                  {p.m === 6 && (
                    <>
                      6 мес <span className="text-brand2">−15%</span>
                    </>
                  )}
                  {p.m === 12 && (
                    <>
                      Год <span className="text-brand2">−20%</span>
                    </>
                  )}
                </button>
              );
            })}
          </div>

          <div className="op-box p-1.5 pl-4 rounded-2xl glass border border-white/10">
            <span className="text-sm">
              <span className="font-semibold">Операторов</span>
              <span className="block text-[11px] text-slate-500 leading-tight">
                1 подписка = 1 оператор
              </span>
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Убрать оператора"
                onClick={() => setOpsSafe(ops - 1)}
                className="grid place-items-center w-9 h-9 rounded-xl border border-white/10 hover:bg-white/5 active:scale-90 transition"
              >
                <Icon id="ic-minus" className="ic ic-sm" />
              </button>
              <input
                type="text"
                inputMode="numeric"
                value={ops}
                aria-label="Количество операторов"
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  if (digits === '') {
                    setOps(1);
                    return;
                  }
                  setOpsSafe(parseInt(digits, 10));
                }}
                className="op-input"
              />
              <button
                type="button"
                aria-label="Добавить оператора"
                onClick={() => setOpsSafe(ops + 1)}
                className="grid place-items-center w-9 h-9 rounded-xl border border-white/10 hover:bg-white/5 active:scale-90 transition"
              >
                <Icon id="ic-plus" className="ic ic-sm" />
              </button>
            </div>
          </div>
        </div>

        <div data-stagger className="grid md:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan) => {
            const base = LANDING_PRICING.plans[plan.id];
            const one = (base * (100 - period.off)) / 100;
            const sum = one * ops * period.m;
            const checkClass = plan.popular ? 'text-brand2' : 'text-emerald-400';

            return (
              <div
                key={plan.id}
                className={`reveal plan rounded-3xl border p-7 flex flex-col ${
                  plan.popular
                    ? 'relative border-brand/50 bg-gradient-to-b from-brand/10 to-panel/40 shadow-2xl shadow-brand/20 md:-translate-y-3'
                    : 'border-white/10 bg-panel/40'
                }`}
                data-plan={plan.id}
                data-base={base}
              >
                {plan.popular && (
                  <>
                    <div className="pointer-events-none absolute inset-0 rounded-3xl overflow-hidden">
                      <div className="sheen-bar" />
                    </div>
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand text-white text-xs font-bold z-10">
                      {i18n.t('billing.plans.popular')}
                    </div>
                  </>
                )}
                <h3 className="font-bold text-lg">{plan.name}</h3>
                <p className="plan-sub text-sm text-slate-400 mt-1 mb-5">{plan.desc}</p>
                <div className="flex items-end gap-2">
                  <span className="price text-4xl font-extrabold">
                    {formatMoney(one)}
                    {rubPart(one) ? <span className="money-rub"> / {rubPart(one)}</span> : null}
                  </span>
                  <span className="text-slate-500 text-sm mb-1.5">{perOpLabel}</span>
                </div>
                {period.off > 0 ? (
                  <div className="disc flex items-center gap-2 mt-2.5">
                    <span className="old text-sm text-slate-500 line-through">{formatMoney(base)}</span>
                    <span className="save text-[11px] font-bold px-2 py-1 rounded-lg bg-emerald-500/12 text-emerald-400 border border-emerald-500/25">
                      −{period.off}% · экономия {formatMoney((base - one) * ops * period.m)}
                    </span>
                  </div>
                ) : null}
                <div className={`total mt-3.5 mb-6 text-[13px] ${plan.popular ? 'text-slate-400' : 'text-slate-500'}`}>
                  <b className="text-slate-200">{formatMoneyDual(sum)}</b>
                  {' · '}
                  {ops} {pluralRu(ops, 'оператор', 'оператора', 'операторов')}
                  {' · '}
                  {period.m} {pluralRu(period.m, 'месяц', 'месяца', 'месяцев')}
                </div>
                <ul className="space-y-2.5 text-sm text-slate-300 mb-7">
                  {plan.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <Icon id="ic-check" className={`ic ic-sm shrink-0 mt-0.5 ${checkClass}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                {isLoggedIn ? (
                  <Link
                    to="/myProfile"
                    className={
                      plan.popular
                        ? 'mt-auto text-center py-3 rounded-2xl bg-brand hover:bg-blue-600 font-semibold shadow-lg shadow-brand/30 transition'
                        : 'mt-auto text-center py-3 rounded-2xl border border-white/15 font-semibold hover:bg-white/5 transition'
                    }
                  >
                    {i18n.t('nav.to_profile')}
                  </Link>
                ) : (
                  <a
                    href="/register"
                    className={
                      plan.popular
                        ? 'mt-auto text-center py-3 rounded-2xl bg-brand hover:bg-blue-600 font-semibold shadow-lg shadow-brand/30 transition'
                        : 'mt-auto text-center py-3 rounded-2xl border border-white/15 font-semibold hover:bg-white/5 transition'
                    }
                  >
                    {ctaLabel}
                  </a>
                )}
              </div>
            );
          })}
        </div>

        <p className="reveal text-center text-xs text-slate-500 mt-7">
          Оплата по счёту: крипта, СБП и банковские карты · смена тарифа в любой момент · автосписаний
          нет
        </p>
      </div>
    </section>
  );
});
